<template>
	<nav v-if="isMobile" class="mobile-nav mobile-tab-bar" data-tour="navigation">
		<button
			v-if="isUserLoggedIn"
			class="tab-item"
			:class="{ active: isActivityActive }"
			:aria-label="notificationAriaLabel"
			:title="notificationAriaLabel"
			data-tour="activity"
			@click="goToActivity"
		>
			<span class="notification-icon-wrapper">
				<AppIcon :name="IconName.Bell" class="tab-icon" />
				<span v-if="showNotificationBadge" class="notification-badge" aria-hidden="true">{{
					notificationBadgeLabel
				}}</span>
			</span>
			<span>{{ $t("nav.activity") }}</span>
		</button>

		<button class="tab-item" :class="{ active: isMapActive }" @click="goToMap">
			<AppIcon :name="IconName.Compass" class="tab-icon" />
			<span>{{ $t("view-exploration") }}</span>
		</button>

		<button
			v-if="canAccessHunting"
			class="tab-item"
			:class="{ active: isHuntActive }"
			data-tour="hunting"
			@click="goToHunt"
		>
			<AppIcon :name="IconName.Crosshair" class="tab-icon" />
			<span>{{ $t("nav.hunt") }}</span>
		</button>

		<button class="tab-item" :class="{ active: isMoreActive }" data-tour="reminder" @click="goToMore">
			<AppIcon :name="IconName.Menu" class="tab-icon" />
			<span>{{ $t("nav.more") }}</span>
		</button>

		<button v-if="!isUserLoggedIn" class="tab-item" @click="handleLogin">
			<AppIcon :name="IconName.LogIn" class="tab-icon" />
			<span>{{ $t("login") }}</span>
		</button>
	</nav>

	<aside
		v-else
		class="app-sidebar side-nav"
		:class="{
			collapsed: isCollapsed,
			expanded: isExpanded,
		}"
	>
		<!-- header -->
		<div class="sidebar-header" @mouseenter="handleMouseEnter" @mouseleave="handleMouseLeave">
			<button
				class="toggle-btn"
				:class="{ active: uiStore.isSidenavLocked }"
				@click.stop="uiStore.toggleSidenavLocked()"
			>
				<img v-if="isCollapsed" :src="listingPinUri" class="brand-pin" alt="Landerz" />
				<AppIcon v-else :name="IconName.Menu" :size="18" :stroke-width="1.25" />
			</button>
			<a class="logo" :class="{ hidden: isCollapsed }" href="https://landerz.ca">
				<img src="@/assets/images/logo.svg" alt="Landerz logo" class="logo-full" />
			</a>
		</div>

		<!-- navigation -->
		<nav class="sidebar-nav" data-tour="navigation" @mouseenter="handleMouseEnter" @mouseleave="handleMouseLeave">
			<div class="nav-section">
				<button
					v-if="isUserLoggedIn"
					class="nav-item"
					:class="{ active: isActivityActive }"
					:aria-label="notificationAriaLabel"
					:title="notificationAriaLabel"
					data-tour="activity"
					@click="goToActivity"
				>
					<span class="notification-icon-wrapper">
						<AppIcon :name="IconName.Bell" class="nav-icon" />
						<span v-if="showNotificationBadge" class="notification-badge" aria-hidden="true"></span>
					</span>
					<span>{{ $t("nav.activity") }}</span>
				</button>

				<button class="nav-item" :class="{ active: isMapActive }" @click="goToMap">
					<AppIcon :name="IconName.Compass" class="nav-icon" />
					<span>{{ $t("view-exploration") }}</span>
				</button>

				<button
					v-if="canAccessHunting"
					class="nav-item"
					:class="{ active: isHuntActive }"
					data-tour="hunting"
					@click="goToHunt"
				>
					<AppIcon :name="IconName.Crosshair" class="nav-icon" />
					<span>{{ $t("nav.hunt") }}</span>
				</button>

				<button
					v-if="config.ui?.has(Feature.ModeTable)"
					class="nav-item"
					:class="{ active: isOpportunitiesActive }"
					data-tour="tables"
					@click="goToTable(TableType.Opportunities)"
				>
					<AppIcon :name="IconName.Flag" class="nav-icon" />
					<span>{{ $t("table.opportunities") }}</span>
				</button>

				<button
					v-if="config.ui?.has(Feature.ModeTable) && rootStore.currentUser?.has(Permission.TransactionRead)"
					class="nav-item"
					:class="{ active: isTransactionsActive }"
					@click="goToTable(TableType.Transactions)"
				>
					<AppIcon :name="IconName.ArrowsSwap" class="nav-icon" />
					<span>{{ $t("table.transactions") }}</span>
				</button>

				<button
					v-if="config.ui?.has(Feature.ModeTable) && rootStore.currentUser?.has(Permission.BuyerRead)"
					class="nav-item"
					:class="{ active: isBuyersActive }"
					@click="goToTable(TableType.Buyers)"
				>
					<AppIcon :name="IconName.User" class="nav-icon" />
					<span>{{ $t("nav.buyers") }}</span>
				</button>

				<button
					v-if="isUserLoggedIn && rootStore.currentUser?.has(Permission.ContactResearchRead)"
					class="nav-item"
					:class="{ active: isContactResearchActive }"
					@click="goToContactResearch"
				>
					<AppIcon :name="IconName.UserSearch" class="nav-icon" />
					<span>{{ $t("nav.contactResearch") }}</span>
				</button>

				<button
					v-if="comparableAnalysisStore.canRead"
					class="nav-item"
					:class="{ active: isAnalysesActive }"
					@click="router.push({ name: Route.ComparableAnalyses })"
				>
					<AppIcon :name="IconName.BarChart" class="nav-icon" /><span>{{ $t("nav.comparables") }}</span>
				</button>
			</div>
		</nav>

		<div
			v-if="showExtendedAccess"
			class="extended-access"
			:title="extendedAccessLabel"
			@mouseenter="handleMouseEnter"
			@mouseleave="handleMouseLeave"
		>
			<span v-if="isCollapsed" class="badge">
				<AppIcon :name="IconName.Sparkles" :size="12" />
				{{ extendedAccessStore.daysLeft }}
			</span>
			<div v-else class="card">
				<AppIcon :name="IconName.Sparkles" class="icon" :size="16" />
				<div class="text">
					<span class="title">{{ $t("extendedAccess.sideNav.title") }}</span>
					<span class="remaining">{{
						$t("extendedAccess.sideNav.daysRemaining", { days: extendedAccessStore.daysLeft })
					}}</span>
				</div>
			</div>
		</div>

		<!-- footer -->
		<div class="sidebar-footer" @mouseenter="handleMouseEnter" @mouseleave="handleMouseLeave">
			<Popover.Root
				v-model:open="isSettingsMenuOpen"
				:positioning="{ placement: 'top-start', gutter: 17, strategy: 'fixed' }"
			>
				<div class="footer-user-card" data-tour="reminder">
					<div v-if="isUserLoggedIn" class="avatar">{{ userInitials }}</div>

					<div v-if="isUserLoggedIn" class="details" :class="{ hidden: isCollapsed }">
						<span class="name">{{ userDisplayName }}</span>
					</div>

					<button v-else class="login-action" @click="handleLogin">
						<AppIcon :name="IconName.LogIn" :size="18" />
						<span class="details" :class="{ hidden: isCollapsed }">{{ $t("login") }}</span>
					</button>

					<Popover.Trigger as-child>
						<button class="trigger" :class="{ active: isSettingsMenuOpen, 'guest-trigger': !isUserLoggedIn }">
							<AppIcon :name="IconName.Settings" :size="18" />
						</button>
					</Popover.Trigger>
				</div>

				<Popover.Positioner>
					<Popover.Content class="popover-content settings-menu" :class="{ collapsed: isCollapsed }">
						<router-link to="/settings" class="item" @click="isSettingsMenuOpen = false">
							<AppIcon :name="IconName.Settings" class="icon" />
							<span>{{ $t("settings") }}</span>
						</router-link>

						<button v-if="isUserLoggedIn && config.ui.has(Feature.AppTour)" class="item" @click="handleStartAppTour">
							<AppIcon :name="IconName.Info" class="icon" />
							<span>{{ $t("appTour.menuLabel") }}</span>
						</button>

						<div v-if="canAccessAdmin" class="divider" />

						<router-link
							v-if="canAccessAdmin"
							:to="adminRoute"
							class="item"
							@click="handleSettingsNavigation('navigation.admin')"
						>
							<AppIcon :name="IconName.Grid" class="icon" />
							<span>{{ $t("adminPage.navLabel") }}</span>
						</router-link>

						<a
							:href="buyerProfileUrl"
							target="_blank"
							class="item"
							@click="handleSettingsNavigation('buyer-profile-action')"
						>
							<AppIcon :name="IconName.UserCircle" class="icon" />
							<span>{{ $t("buyer-profile-action") }}</span>
						</a>

						<a
							href="https://www.landerz.ca/privacypolicy"
							target="_blank"
							class="item"
							@click="handleSettingsNavigation('terms')"
						>
							<AppIcon :name="IconName.FileText" class="icon" />
							<span>{{ $t("terms") }}</span>
						</a>

						<template v-if="isUserLoggedIn">
							<div class="divider" />

							<button class="item logout" @click="handleMenuLogout">
								<AppIcon :name="IconName.LogOut" class="icon" :stroke-width="2" />
								<span>{{ $t("logout") }}</span>
							</button>
						</template>
					</Popover.Content>
				</Popover.Positioner>
			</Popover.Root>
		</div>
	</aside>
</template>

<script setup lang="ts">
import { useComparableAnalysisStore } from "@/stores/comparableAnalysisStore"
import { computed, inject, ref, watch } from "vue"
import { useRouter, useRoute } from "vue-router"
import { useI18n } from "vue-i18n"
import { useMediaQuery } from "@vueuse/core"
import { Popover } from "@ark-ui/vue"
import AppIcon from "@/components/AppIcon.vue"
import { IconName } from "@/components/icons/registry"
import { useUIStore } from "@/stores/uiStore"
import { config, Feature } from "@/AppConfig"
import { analytics } from "@/utils/analytics"
import { Mode, Permission, TableType, UseRootStore, isTableType } from "@/model/RootStore"
import { ProjectType } from "@/graphql"
import { buildLanderzMarkDataUri } from "@/utils/opportunityIcons"
import { ProjectStoreKey } from "@/stores/projectStore"
import { getUserInitials } from "@/utils/user"
import { Route, firstQueryValue } from "@/utils/routing"
import { hasAdminAccess } from "@/utils/adminAccess"
import { useExtendedAccessStore } from "@/stores/extendedAccessStore"

interface Props {
	currentUser?: any
	store?: any
	userProjects?: any[]
	currentProject?: any
	isUserLoggedIn?: boolean
	mode?: Mode
	tableType?: TableType
	notificationCount?: number
}

const props = withDefaults(defineProps<Props>(), {
	userProjects: () => [],
	isUserLoggedIn: false,
	notificationCount: 0,
})

const isMobile = useMediaQuery("(max-width: 768px)")

const uiStore = useUIStore()
const router = useRouter()
const route = useRoute()
const rootStore = UseRootStore()
const { locale, t } = useI18n()
const projectStore = inject(ProjectStoreKey)!()
const { exitProject } = projectStore

const listingPinUri = buildLanderzMarkDataUri()

const isSettingsMenuOpen = ref(false)

const userInitials = computed(() => {
	const info = props.currentUser?.getInfo?.()
	const fullName = [info?.firstName, info?.lastName].filter(Boolean).join(" ")

	return getUserInitials(fullName)
})

const userDisplayName = computed(() => {
	const info = props.currentUser?.getInfo?.()
	if (!info?.firstName) return info?.email ?? ""
	return info.firstName
})

const buyerProfileUrl = computed(() =>
	locale.value === "en" ? "https://en.landerz.ca/profil" : "https://www.landerz.ca/profil"
)

const adminRoute = computed(() => {
	const projectId = firstQueryValue(route.query.project)
	return { path: "/admin", query: projectId ? { project: projectId } : {} }
})

// TEMP: SideNavLayout doesn't pass notificationCount, read from rootStore directly for visual testing, revert before commit
const showNotificationBadge = computed(() => rootStore.unreadNotificationCount > 0)

const notificationBadgeLabel = computed(() =>
	rootStore.unreadNotificationCount > 99 ? "99+" : String(rootStore.unreadNotificationCount)
)

const notificationAriaLabel = computed(() =>
	rootStore.unreadNotificationCount > 0 ? `Notifications (${notificationBadgeLabel.value} unread)` : "Notifications"
)

const extendedAccessStore = useExtendedAccessStore()
watch(
	() => rootStore.userApiToken,
	() => {
		extendedAccessStore.load()
	},
	{ immediate: true }
)

const showExtendedAccess = computed(() => props.isUserLoggedIn && extendedAccessStore.isActive)

const extendedAccessLabel = computed(
	() =>
		`${t("extendedAccess.sideNav.title")} · ${t("extendedAccess.sideNav.daysRemaining", { days: extendedAccessStore.daysLeft })}`
)

const comparableAnalysisStore = useComparableAnalysisStore()

const canAccessHunting = computed(() =>
	props.userProjects.some((p: { type?: string }) => p.type === "CCA" || p.type === "WORKSPACE")
)

const canAccessAdmin = computed(() => props.isUserLoggedIn && hasAdminAccess(rootStore.currentUser))

const isCollapsed = computed(() => {
	return !uiStore.isSidenavExpanded && !uiStore.isSidenavLocked
})

const isExpanded = computed(() => {
	return !isCollapsed.value
})

const handleMouseEnter = () => {
	uiStore.isSidenavExpanded = true
}

const handleMouseLeave = () => {
	uiStore.isSidenavExpanded = false
}

const isHomeRoute = computed(() => route.path === "/")
const routeTableType = computed(() => {
	const value = firstQueryValue(route.query.table)
	return isTableType(value) ? value : null
})

// URL-driven so a reload on /?project= highlights "Hunt" immediately, without waiting
// for currentProject to resolve (which would briefly flash "Exploration" as active).
const hasProjectQuery = computed(() => firstQueryValue(route.query.project) != null)
const isViewingHuntProject = computed(
	() => isHomeRoute.value && (hasProjectQuery.value || props.currentProject?.type === ProjectType.Cca)
)
const isMapActive = computed(() => isHomeRoute.value && !routeTableType.value && !isViewingHuntProject.value)
const isOpportunitiesActive = computed(
	() => isHomeRoute.value && routeTableType.value === TableType.Opportunities && !isViewingHuntProject.value
)
const isTransactionsActive = computed(
	() => isHomeRoute.value && routeTableType.value === TableType.Transactions && !isViewingHuntProject.value
)
const isBuyersActive = computed(
	() => isHomeRoute.value && routeTableType.value === TableType.Buyers && !isViewingHuntProject.value
)
const isContactResearchActive = computed(() => route.name === Route.ContactResearch)
const isHuntActive = computed(() => route.path === "/hunting" || isViewingHuntProject.value)
const isActivityActive = computed(() => route.path === "/activity")
const isMoreActive = computed(() => route.path === "/more")
const isAnalysesActive = computed(() => route.path.startsWith("/comparable-analyses"))

function goToMap(): void {
	analytics.navigationClicked("view-exploration")
	if (isViewingHuntProject.value) {
		exitProject(null)

		return
	}
	const query = { ...route.query }
	delete query.table
	router.push({ name: Route.Home, query })
}

function goToTable(tableType: TableType): void {
	analytics.navigationClicked(tableType === TableType.Buyers ? "table.buyers" : "view-table")
	if (isViewingHuntProject.value) {
		exitProject(tableType)

		return
	}
	router.push({ name: Route.Home, query: { ...route.query, table: tableType } })
}

function goToHunt(): void {
	analytics.navigationClicked("project.my-projects")
	router.push({ name: Route.Hunting })
}

function goToActivity(): void {
	router.push({ name: Route.Activity })
}

function goToContactResearch(): void {
	router.push({ name: Route.ContactResearch })
}

function goToMore(): void {
	router.push({ name: Route.More })
}

function handleSettingsNavigation(translationKey: string): void {
	isSettingsMenuOpen.value = false
	analytics.navigationClicked(translationKey)
}

async function handleStartAppTour(): Promise<void> {
	isSettingsMenuOpen.value = false
	analytics.navigationClicked("appTour.menuLabel")
	if (!isHomeRoute.value || routeTableType.value) await router.push({ name: Route.Home })
	uiStore.startAppTour()
}

const handleLogin = () => {
	router.push({ name: Route.Login })
}

const handleLogout = async () => {
	try {
		await rootStore.logout()
		router.push({ name: Route.Home })
	} catch (error) {
		console.error("Error during logout:", error)
		router.push({ name: Route.Login })
	}
}

const handleMenuLogout = async () => {
	await handleLogout()
	isSettingsMenuOpen.value = false
}
</script>

<style scoped>
.mobile-tab-bar {
	position: fixed;
	left: 0;
	right: 0;
	bottom: 0;
	z-index: 100;
	display: flex;
	align-items: center;
	height: calc(var(--space-56) + env(safe-area-inset-bottom));
	background: var(--neutral-0);
	border-top: var(--border-width-thin) solid var(--neutral-100);
	padding-bottom: env(safe-area-inset-bottom);
	font-family: var(--font-family-primary);

	.tab-item {
		flex: 1;
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: var(--spacing-xxs);
		padding: var(--spacing-sm) 0;
		border: none;
		background: none;
		color: var(--neutral-600);
		font-size: var(--font-size-xs);
		cursor: pointer;

		.tab-icon {
			color: currentColor;
		}

		.notification-icon-wrapper {
			position: relative;
			display: inline-flex;
			align-items: center;
			justify-content: center;
		}

		.notification-badge {
			position: absolute;
			top: calc(var(--spacing-xs) * -1);
			right: calc(var(--spacing-xs) * -1);
			min-width: var(--spacing-md);
			height: var(--spacing-md);
			border: var(--border-width-thin) solid var(--neutral-0);
			border-radius: var(--border-radius-full);
			background: var(--accent-warning);
			color: var(--neutral-0);
			display: inline-flex;
			align-items: center;
			justify-content: center;
			padding: 0 calc(var(--spacing-xs) * 0.5);
			font-size: var(--font-size-xs);
			font-weight: var(--font-weight-bold);
			line-height: 1;
		}

		&.active {
			color: var(--primary-green);
			font-weight: var(--font-weight-medium);
		}
	}
}

.app-sidebar {
	width: var(--width-nav-side);
	height: 100vh;
	position: fixed;
	left: 0;
	top: 0;
	background: var(--neutral-0);
	display: flex;
	flex-direction: column;
	border-right: var(--border-width-thin) solid var(--neutral-200);
	z-index: 100;
	overflow: hidden;
	transition: width 0.3s ease;
	font-family: var(--font-family-primary);

	&.collapsed {
		width: var(--width-nav-collapsed);

		.sidebar-nav .nav-section .nav-item {
			span {
				opacity: 0;
				pointer-events: none;
			}

			.notification-icon-wrapper {
				opacity: 1;
				width: auto;
				overflow: visible;
			}

			.notification-badge {
				opacity: 1;
				overflow: visible;
				width: var(--spacing-sm);
				height: var(--spacing-sm);
			}
		}

		.footer-user-card .trigger {
			opacity: 0;
			pointer-events: none;
		}

		.footer-user-card .trigger.guest-trigger {
			display: none;
		}
	}

	.toggle-btn {
		display: flex;
		align-items: center;
		justify-content: center;
		width: var(--size-icon-xl);
		height: var(--size-icon-xl);
		border: none;
		background: none;
		border-radius: var(--border-radius-sm);
		color: var(--neutral-600);
		cursor: pointer;
		flex-shrink: 0;
		transition:
			background 0.15s ease,
			color 0.15s ease;

		&.active {
			color: var(--primary-green);
		}

		.brand-pin {
			width: var(--space-40);
			height: var(--space-40);
			object-fit: contain;
		}

		&:hover {
			background: var(--neutral-100);

			&.active {
				background: var(--accent-highlight);
			}
		}
	}

	.sidebar-header {
		padding: var(--spacing-md) var(--spacing-sm);
		padding-left: var(--space-14);
		border-bottom: var(--border-width-thin) solid var(--neutral-200);
		display: flex;
		align-items: center;
		gap: var(--spacing-sm);
		flex-shrink: 0;

		.logo {
			display: flex;
			align-items: center;

			&.hidden {
				opacity: 0;
				width: 0;
				overflow: hidden;
				padding-left: none;
			}

			.logo-full {
				height: clamp(15.84px, 0.9375vw, 18px); /* 88% floor */
				width: auto;
				object-fit: contain;
				margin: 0 0 var(--spacing-xs) var(--spacing-xs);
			}
		}
	}

	.sidebar-nav {
		flex: 1;
		overflow-y: auto;
		overflow-x: hidden;
		scrollbar-width: none;
		padding: var(--spacing-sm) 0;
		min-height: 0;

		&::-webkit-scrollbar {
			display: none;
		}

		.nav-section {
			margin-bottom: var(--spacing-sm);
			display: flex;
			flex-direction: column;
			gap: var(--spacing-xs);

			.nav-item {
				display: flex;
				align-items: center;
				gap: var(--spacing-sm);
				padding: var(--spacing-smd);
				width: calc(100% - calc(var(--spacing-xs) * 2));
				margin: 0 var(--spacing-xs);
				border: none;
				border-radius: var(--border-radius-sm);
				background: none;
				text-align: left;
				color: var(--neutral-600);
				text-decoration: none;
				cursor: pointer;
				transition:
					background 0.15s ease,
					color 0.15s ease;
				font-size: var(--font-size-regular);
				font-weight: var(--font-weight-regular);
				line-height: var(--line-height-normal);
				position: relative;

				.nav-icon {
					flex-shrink: 0;
					width: var(--size-icon-lg);
					height: var(--size-icon-lg);
					color: currentColor;
				}

				span {
					flex: 1;
					overflow: hidden;
					text-overflow: ellipsis;
					white-space: nowrap;
					transition: opacity 0.2s ease;
				}

				.notification-icon-wrapper {
					position: relative;
					display: inline-flex;
					align-items: center;
					justify-content: center;
					flex: 0 0 auto;
					width: var(--size-icon-lg);
					height: var(--size-icon-lg);
					overflow: visible;
				}

				.notification-badge {
					position: absolute;
					top: calc(var(--spacing-xs) * -1);
					right: calc(var(--spacing-xs) * -1);
					transform: translate(15%, -25%);
					width: var(--spacing-sm);
					height: var(--spacing-sm);
					border-radius: var(--border-radius-full);
					background: var(--primary-green);
				}

				&:hover {
					background: var(--neutral-100);
					color: var(--neutral-900);
				}

				&.active {
					background: var(--accent-highlight);
					color: var(--primary-green);
					font-weight: var(--font-weight-medium);

					.notification-badge {
						background-color: var(--primary-green);
						color: var(--neutral-0);
					}
				}
			}
		}
	}

	.extended-access {
		flex-shrink: 0;
		display: flex;
		align-items: center;
		justify-content: flex-start;
		height: var(--space-56);
		padding: 0 var(--spacing-xs);

		.badge {
			display: flex;
			align-items: center;
			justify-content: center;
			gap: var(--space-2);
			box-sizing: border-box;
			width: 100%;
			height: var(--space-48);
			border-radius: var(--border-radius-sm);
			background: color-mix(in srgb, var(--primary-green) 8%, transparent);
			color: var(--primary-green);
			font-size: var(--font-size-xs);
			font-weight: var(--font-weight-semibold);
		}

		.card {
			display: flex;
			align-items: center;
			gap: var(--spacing-sm);
			box-sizing: border-box;
			width: 100%;
			height: var(--space-48);
			padding: 0 var(--spacing-sm);
			border-radius: var(--border-radius-sm);
			background: color-mix(in srgb, var(--primary-green) 8%, transparent);

			.icon {
				flex-shrink: 0;
				color: var(--primary-green);
			}

			.text {
				display: flex;
				flex-direction: column;
				min-width: 0;

				.title {
					font-size: var(--font-size-small);
					font-weight: var(--font-weight-semibold);
					color: var(--primary-green);
					white-space: nowrap;
				}

				.remaining {
					font-size: var(--font-size-xs);
					color: var(--neutral-600);
					white-space: nowrap;
				}
			}
		}
	}

	.sidebar-footer {
		border-top: var(--border-width-thin) solid var(--neutral-200);
		position: relative;
		padding: var(--space-6) 0;
		flex-shrink: 0;
		margin-top: auto;

		.footer-user-card {
			display: flex;
			align-items: center;
			gap: var(--space-6);
			padding: 0 var(--space-10) 0 var(--space-14);
			height: var(--space-56);
		}

		.login-action {
			display: flex;
			align-items: center;
			gap: var(--spacing-sm);
			flex: 1;
			min-width: 0;
			padding: 0;
			border: none;
			background: none;
			text-align: left;
			color: var(--neutral-600);
			cursor: pointer;
			transition: color 0.15s ease;
			font-size: var(--font-size-regular);
			font-weight: var(--font-weight-regular);
			line-height: var(--line-height-normal);

			&:hover {
				color: var(--neutral-900);
			}
		}

		.avatar {
			display: flex;
			align-items: center;
			justify-content: center;
			flex-shrink: 0;
			width: var(--size-icon-xl);
			height: var(--size-icon-xl);
			border-radius: var(--border-radius-full);
			background: var(--primary-green);
			color: var(--neutral-0);
			font-size: var(--font-size-small);
			font-weight: var(--font-weight-semibold);
		}

		.details {
			display: flex;
			flex-direction: column;
			min-width: 0;
			flex: 1;
			overflow: hidden;
			transition: opacity 0.15s ease;

			&.hidden {
				opacity: 0;
				pointer-events: none;
			}

			.name {
				font-size: var(--font-size-small);
				font-weight: var(--font-weight-medium);
				color: var(--neutral-900);
				line-height: var(--line-height-normal);
				overflow: hidden;
				text-overflow: ellipsis;
				white-space: nowrap;
			}
		}

		.trigger {
			display: flex;
			align-items: center;
			justify-content: center;
			flex-shrink: 0;
			margin-left: auto;
			padding: var(--spacing-xs);
			border: none;
			border-radius: var(--border-radius-sm);
			background: none;
			color: var(--neutral-600);
			cursor: pointer;
			transition:
				background 0.15s ease,
				color 0.15s ease,
				opacity 0.15s ease;

			&:hover,
			&.active {
				background: var(--neutral-100);
				color: var(--neutral-900);
			}
		}
	}

	@media (max-width: 768px) {
		transform: translateX(-100%);
		transition: transform 0.3s ease;
	}
}

.settings-menu {
	width: 180px;
	padding: var(--spacing-xs);
	background-color: var(--neutral-0);
	border: var(--border-width-thin) solid var(--neutral-200);
	border-radius: var(--border-radius-sm);
	box-shadow: var(--shadow-sm);
	z-index: 1000;
	animation: slideUp 0.15s ease-out;

	&.collapsed {
		margin-left: calc(var(--spacing-md) * -1);
	}

	.item {
		display: flex;
		align-items: center;
		gap: var(--spacing-sm);
		padding: var(--spacing-sm);
		border: none;
		border-radius: var(--border-radius-sm);
		background: none;
		width: 100%;
		text-align: left;
		text-decoration: none;
		color: var(--neutral-600);
		cursor: pointer;
		font-size: var(--font-size-regular);
		font-weight: var(--font-weight-regular);
		transition:
			background 0.15s ease,
			color 0.15s ease;

		.icon {
			flex-shrink: 0;
			width: var(--size-icon-md);
			height: var(--size-icon-md);
			color: currentColor;
		}

		&:hover {
			background: var(--neutral-100);
			color: var(--neutral-900);
		}

		&.logout {
			color: var(--accent-warning);

			&:hover {
				background: var(--neutral-100);
				color: var(--accent-warning);
			}
		}
	}

	.divider {
		height: var(--border-width-thin);
		background: var(--neutral-200);
		margin: var(--spacing-xs) 0;
	}
}

@keyframes slideUp {
	from {
		opacity: 0;
		transform: translateY(8px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}
</style>
